// The consent record, `settings/analytics.json` in the storage API, and the
// decision made from it on load. Pure: the text in and out, the decision
// from the record and the region; the storage calls, the country lookup and
// the tag are the entry point's.
//
// The decision: a granted record starts analytics, a denied one keeps it
// off, and without a record it depends on where the visitor is
// (`regions.ts`): where consent is required the sheet asks, elsewhere
// analytics is on by implied consent and nothing is written until the user
// touches the switch in Settings.

export const CONSENT_PATH = 'settings/analytics.json';

export type Consent = 'granted' | 'denied';
/** Where the visitor was when the record was written: `required`, the sheet's regions, or `free`, the rest. */
export type ConsentRegion = 'required' | 'free';

export interface ConsentRecord {
  consent: Consent;
  /** When it was written, ISO 8601. */
  at: string;
  region?: ConsentRegion;
}

export type Decision = 'on' | 'off' | 'ask';

/** The record a file holds, or undefined for no file, bad JSON or anything but a grant or a denial. The first records had no `region`. */
export function parseConsent(text: string | undefined): ConsentRecord | undefined {
  if (!text) return undefined;
  try {
    const value: unknown = JSON.parse(text);
    if (typeof value !== 'object' || value === null) return undefined;
    const { consent, at, region } = value as Record<string, unknown>;
    if (consent !== 'granted' && consent !== 'denied') return undefined;
    const record: ConsentRecord = { consent, at: typeof at === 'string' ? at : '' };
    if (region === 'required' || region === 'free') record.region = region;
    return record;
  } catch {
    return undefined;
  }
}

/** The file's text for a grant or a denial made now, with the region it was made in. */
export function consentRecord(consent: Consent = 'granted', region?: ConsentRegion, now = new Date()): string {
  const record: ConsentRecord = { consent, at: now.toISOString() };
  if (region) record.region = region;
  return JSON.stringify(record);
}

/** What to do on load: `on` starts the tag, `ask` shows the sheet, `off` leaves the no-op. */
export function decide({ record, required }: { record: ConsentRecord | undefined; required: boolean }): Decision {
  if (record?.consent === 'granted') return 'on';
  if (record?.consent === 'denied') return 'off';
  return required ? 'ask' : 'on';
}
